import { useMergeRefs } from '@floating-ui/react';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import useSelectionHover from 'articleEditor/hooks/useSelectionHover';
import cx from 'classnames';
import { CSSProperties, HTMLAttributes, useLayoutEffect } from 'react';

import s from './floatingDiv.module.scss';

type Props = HTMLAttributes<HTMLDivElement> & {
  floatingProps: ReturnType<typeof useSelectionHover>;
  /**
   * Disabled the animation when isOpen is false, can prevent the animated
   * object sliding
   */
  withDisabling?: boolean;
};

/** A div that positions itself with @floating-ui hooks */
const FloatingDiv = ({
  floatingProps,
  className,
  children,
  withDisabling,
  ...props
}: Props): JSX.Element | null => {
  const [floatingParent, enable] = useAutoAnimate<HTMLDivElement>({
    duration: 100,
  });
  const { isOpen, strategy, hide, refs, x, y } = floatingProps;
  const mergedRef = useMergeRefs([refs.setFloating, floatingParent]);

  // Prevents the animation "sliding"
  useLayoutEffect(() => {
    if (!withDisabling) return;
    enable(!!isOpen);
  }, [isOpen, enable, withDisabling]);

  const style: CSSProperties = {
    position: strategy,
    top: y ?? 0,
    left: x ?? 0,
    opacity: 1,
  };

  return (
    <div
      className={cx(s['floatingDiv'], className)}
      ref={mergedRef}
      style={style}
      // make sure Slate is happy to have div's added
      contentEditable={false}
      {...props}
    >
      {isOpen && !hide && children}
    </div>
  );
};
FloatingDiv.displayName = 'FloatingDiv';

export { FloatingDiv };
